import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sparkles, Plug, Settings, Users, ArrowRight } from "lucide-react";

interface PreviewBannerProps {
  title?: string;
  description: string;
  ctaLabel?: string;
  ctaHref?: string;
  showConnectCta?: boolean;
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  agencyHint?: {
    body: string;
    ctaLabel: string;
    ctaHref: string;
  };
}

export default function PreviewBanner({
  title,
  description,
  ctaLabel,
  ctaHref = "/integracje",
  showConnectCta = true,
  secondaryLabel,
  onSecondaryClick,
  agencyHint,
}: PreviewBannerProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("previewBanner.sampleData");
  const resolvedCta = ctaLabel ?? t("previewBanner.connectData");
  return (
    <div className="rounded-2xl border border-[#ef3e29]/25 bg-gradient-to-r from-[#ef3e29]/10 via-[#ef3e29]/[0.06] to-transparent p-4 md:p-5 space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ef3e29]/30 bg-[#ef3e29]/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#ffb0a5]">
            <Sparkles className="h-3.5 w-3.5" />
            {resolvedTitle}
          </div>
          <p className="max-w-3xl text-sm text-white/75">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showConnectCta && (
            <Link href={ctaHref}>
              <Button className="bg-[#ef3e29] text-white hover:bg-[#d63520]">
                <Plug className="mr-2 h-4 w-4" />
                {resolvedCta}
              </Button>
            </Link>
          )}
          {secondaryLabel && onSecondaryClick && (
            <Button
              variant="outline"
              onClick={onSecondaryClick}
              className="border-white/[0.14] bg-white/[0.03] text-white/80 hover:bg-white/[0.06] hover:text-white"
            >
              <Settings className="mr-2 h-4 w-4" />
              {secondaryLabel}
            </Button>
          )}
        </div>
      </div>
      {agencyHint && (
        <div className="flex flex-col gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-lg bg-[#6E3AFF]/20 p-2">
              <Users className="h-4 w-4 text-[#a888ff]" />
            </div>
            <p className="text-xs leading-relaxed text-white/70">{agencyHint.body}</p>
          </div>
          <Link href={agencyHint.ctaHref}>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-[#6E3AFF]/40 bg-[#6E3AFF]/10 text-[#c9b3ff] hover:bg-[#6E3AFF]/20 hover:text-white"
            >
              {agencyHint.ctaLabel}
              <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
